import { cn } from '@veraclins-dev/utils';

import { type IconName } from '../icons';
import { Box } from '../ui/box';
import { type IconProps } from '../ui/icon';
import { Label, type LabelProps } from '../ui/label';
import { Typography } from '../ui/typography';
import {
  inputContainerVariants,
  type InputVariants,
} from '../ui/utils/variants/input';
import { extractStyleProps } from '../ui/utils/variants/styles';

import { ErrorList } from './error-list';

// The presentational (non-`field`) half of a form field's wrapper props. Spelled
// out here rather than derived from `InputFieldWrapperProps` (now in
// @veraclins-dev/form) — ui cannot depend on form, and `InputWrapper` is the bare
// primitive `InputFieldWrapper` builds on.
export interface InputWrapperProps extends React.ComponentProps<'div'> {
  children: React.ReactNode;
  leftIcon?: IconProps | IconName;
  rightIcon?: IconProps | IconName;
  rightAddon?: React.ReactNode;
  name?: string;
  label?: string | React.ReactNode;
  topText?: string | React.ReactNode;
  labelProps?: LabelProps;
  inputClass?: string;
  wrapperClassName?: string;
  plain?: boolean;
  inputProps?: InputVariants & {
    className?: string;
  };
  errorId?: string;
  id: string;
  labelId?: string;
  errors?: string[];
  containerRef?: React.Ref<HTMLDivElement>;
}

/**
 * Presentational shell for a form control: label row, styled input container,
 * and error list, wired to caller-supplied ids. Use it to build custom fields
 * when the conform-bound `InputFieldWrapper` does not fit.
 * @category forms
 */
export const InputWrapper = ({
  className,
  children,
  label,
  labelProps,
  topText,
  ref,
  wrapperClassName,
  plain,
  errorId,
  id,
  labelId,
  errors,
  containerRef,
  inputProps,
  ...props
}: InputWrapperProps) => {
  const { styleProps, others } = extractStyleProps(inputProps ?? {});
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      className={cn('relative w-full', wrapperClassName)}
      ref={ref}
      data-slot="input-wrapper"
      {...props}
    >
      {(label || topText) && (
        <Box
          display="flex"
          justify="between"
          items="baseline"
          flexWrap="wrap"
          gapX={3}
          gapY={1}
        >
          {label && (
            <Label
              variant="field"
              {...labelProps}
              id={labelId}
              htmlFor={id}
              // Field labels stack above the control (`block` overrides the
              // Label base `flex`). Weight/cursor come from the Label variant
              // itself, so we don't hardcode them here — that lets a caller's
              // `labelProps={{ variant: 'caption' }}` keep its own treatment.
              className={cn('block', labelProps?.className)}
            >
              {label}
            </Label>
          )}
          {topText && (
            <Typography variant="caption" className="ms-auto italic">
              {topText}
            </Typography>
          )}
        </Box>
      )}
      <Box
        data-state={errorId ? 'error' : undefined}
        className={cn(
          !plain &&
            inputContainerVariants({
              ...styleProps,
              inputSize: others.inputSize,
              surface: others.surface,
              className: others.className,
            }),
          className,
        )}
        ref={containerRef}
      >
        {children}
      </Box>
      <ErrorList id={errorId} errors={errorId ? errors : undefined} />
    </Box>
  );
};
